import { message } from 'antd';
import { all, call, put, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import {
  ICurrentOrganisation,
  IOrganisationUser,
  IOrganisationUserInvitation,
  IPEPHealthService,
} from '@liquid-state/pephealth-client';
import doCreatePEPHealthService from '@redux/doCreatePEPHealthService';
import { SESSION_ESTABLISHED } from '@redux/login/actions';
import i18n from '../../i18n';
import {
  DirectChildOrganisation,
  getDirectChildOrganisation,
  listDirectChildOrganisations,
} from './client';
import {
  CREATE_DIRECT_CHILD_ORGANISATION,
  EDIT_DIRECT_CHILD_ORGANISATION_USER,
  INVITE_DIRECT_CHILD_ORGANISATION_USER,
  LOAD_CURRENT_ORGANISATION,
  LOAD_DIRECT_CHILD_ORGANISATIONS,
  LOAD_DIRECT_CHILD_ORGANISATION,
  LOAD_DIRECT_CHILD_ORGANISATION_USERS,
  LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS,
  RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION,
  createDirectChildOrganisationFailure,
  createDirectChildOrganisationSuccess,
  editDirectChildOrganisationUserFailure,
  editDirectChildOrganisationUserSuccess,
  inviteDirectChildOrganisationUserFailure,
  inviteDirectChildOrganisationUserSuccess,
  loadDirectChildOrganisationFailure,
  loadDirectChildOrganisationSuccess,
  loadDirectChildOrganisationsFailure,
  loadDirectChildOrganisationsSuccess,
  loadCurrentOrganisationFailure,
  loadCurrentOrganisationSuccess,
  loadDirectChildOrganisationUsers,
  loadDirectChildOrganisationUsersFailure,
  loadDirectChildOrganisationUsersSuccess,
  loadDirectChildOrganisationUserDetails,
  loadDirectChildOrganisationUserDetailsFailure,
  loadDirectChildOrganisationUserDetailsSuccess,
  resendDirectChildOrganisationUserInvitationFailure,
  resendDirectChildOrganisationUserInvitationSuccess,
} from './actions';

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to load the current Organisation';
}

function errorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;

  const serviceError = error as { response?: { status?: number }; status?: number };
  return serviceError.response?.status ?? serviceError.status;
}

async function errorCode(error: unknown): Promise<string | undefined> {
  if (typeof error !== 'object' || error === null) return undefined;

  const serviceError = error as {
    code?: string;
    response?: {
      data?: { error_code?: string; code?: string };
      json?: () => Promise<{ error_code?: string; code?: string }>;
    };
  };
  const response = serviceError.response;

  if (response?.data) {
    return response.data.error_code ?? response.data.code ?? serviceError.code;
  }
  if (!response?.json) return serviceError.code;

  try {
    const body = await response.json();
    return body.error_code ?? body.code ?? serviceError.code;
  } catch (_error) {
    return serviceError.code;
  }
}

export function* doLoadCurrentOrganisation(): any {
  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    const organisation: ICurrentOrganisation = yield call(service.getCurrentOrganisation);
    yield put(loadCurrentOrganisationSuccess(organisation));
  } catch (error) {
    yield put(loadCurrentOrganisationFailure(errorMessage(error)));
  }
}

export function* doLoadDirectChildOrganisations(): any {
  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    const organisations: DirectChildOrganisation[] = yield call(
      listDirectChildOrganisations,
      service,
    );
    yield put(loadDirectChildOrganisationsSuccess(organisations));
  } catch (error) {
    const code: string | undefined = yield call(errorCode, error);
    yield put(loadDirectChildOrganisationsFailure(errorMessage(error), errorStatus(error), code));
  }
}

export function* doLoadDirectChildOrganisation(
  action: ReturnType<typeof import('./actions').loadDirectChildOrganisation>,
): any {
  const { organisationSlug } = action.payload;

  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    const organisation: DirectChildOrganisation = yield call(
      getDirectChildOrganisation,
      service,
      organisationSlug,
    );
    yield put(loadDirectChildOrganisationSuccess(organisation));
  } catch (error) {
    yield put(
      loadDirectChildOrganisationFailure(organisationSlug, errorMessage(error), errorStatus(error)),
    );
  }
}

export function* doCreateDirectChildOrganisation(
  action: ReturnType<typeof import('./actions').createDirectChildOrganisation>,
): any {
  const { name, language } = action.payload;

  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    yield call(service.createOrganisation, name, language);
    yield put(createDirectChildOrganisationSuccess());
    yield call(
      message.success,
      i18n.t('directChildOrganisationAdministration:organisations.creation.success'),
    );
  } catch (error) {
    const status = errorStatus(error);
    const errorKey = status === 403 ? 'forbidden' : status === 502 ? 'upstream' : 'failure';

    yield put(createDirectChildOrganisationFailure());
    yield call(
      message.error,
      i18n.t(`directChildOrganisationAdministration:organisations.creation.${errorKey}`),
    );
  }
}

export function* doLoadDirectChildOrganisationUsers(): any {
  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    const organisations: DirectChildOrganisation[] = yield call(
      listDirectChildOrganisations,
      service,
    );
    const usersByOrganisation: IOrganisationUser[][] = yield all(
      organisations.map(organisation => call(service.listOrganisationUsers, organisation.slug)),
    );

    yield put(loadDirectChildOrganisationsSuccess(organisations));
    yield put(
      loadDirectChildOrganisationUsersSuccess(
        organisations.map((organisation, index) => ({
          organisation,
          users: usersByOrganisation[index],
        })),
      ),
    );
  } catch (error) {
    yield put(loadDirectChildOrganisationUsersFailure(errorMessage(error), errorStatus(error)));
  }
}

export function* doInviteDirectChildOrganisationUser(
  action: ReturnType<typeof import('./actions').inviteDirectChildOrganisationUser>,
): any {
  const { organisationSlug, invite } = action.payload;

  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    yield call(service.inviteOrganisationUser, organisationSlug, invite);
    yield put(loadDirectChildOrganisationUsers());
    yield put(inviteDirectChildOrganisationUserSuccess());
    yield call(
      message.success,
      i18n.t('directChildOrganisationAdministration:users.invite.success'),
    );
  } catch (error) {
    const status = errorStatus(error);
    const errorKey =
      status === 403
        ? 'forbidden'
        : status === 404
        ? 'notFound'
        : status === 409
        ? 'conflict'
        : status === 502
        ? 'upstream'
        : 'failure';

    yield put(inviteDirectChildOrganisationUserFailure());
    yield call(
      message.error,
      i18n.t(`directChildOrganisationAdministration:users.invite.${errorKey}`),
    );
  }
}

export function* doLoadDirectChildOrganisationUserDetails(
  action: ReturnType<typeof import('./actions').loadDirectChildOrganisationUserDetails>,
): any {
  const { organisationSlug, userId } = action.payload;

  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    const [organisation, users, invitations]: [
      DirectChildOrganisation,
      IOrganisationUser[],
      IOrganisationUserInvitation[],
    ] = yield all([
      call(getDirectChildOrganisation, service, organisationSlug),
      call(service.listOrganisationUsers, organisationSlug),
      call(service.listOrganisationUserInvitations, organisationSlug, userId),
    ]);
    const user = users.find(candidate => String(candidate.id) === String(userId));

    if (!user) {
      yield put(
        loadDirectChildOrganisationUserDetailsFailure(
          organisationSlug,
          userId,
          'Organisation User not found',
          404,
        ),
      );
      return;
    }

    yield put(loadDirectChildOrganisationUserDetailsSuccess(organisation, user, invitations));
  } catch (error) {
    yield put(
      loadDirectChildOrganisationUserDetailsFailure(
        organisationSlug,
        userId,
        errorMessage(error),
        errorStatus(error),
      ),
    );
  }
}

export function* doResendDirectChildOrganisationUserInvitation(
  action: ReturnType<typeof import('./actions').resendDirectChildOrganisationUserInvitation>,
): any {
  const { organisationSlug, userId } = action.payload;

  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    yield call(service.resendOrganisationUserInvitation, organisationSlug, userId);
    yield call(
      doLoadDirectChildOrganisationUserDetails,
      loadDirectChildOrganisationUserDetails(organisationSlug, userId),
    );
    yield put(resendDirectChildOrganisationUserInvitationSuccess(organisationSlug, userId));
    yield call(
      message.success,
      i18n.t('directChildOrganisationAdministration:users.details.resend.success'),
    );
  } catch (error) {
    const status = errorStatus(error);
    const code: string | undefined = yield call(errorCode, error);
    const alreadyActivated = status === 409 && code === 'USER_ALREADY_ACTIVATED';

    if (alreadyActivated) {
      yield call(
        doLoadDirectChildOrganisationUserDetails,
        loadDirectChildOrganisationUserDetails(organisationSlug, userId),
      );
    }

    const errorKey = alreadyActivated
      ? 'alreadyActivated'
      : status === 403
      ? 'forbidden'
      : status === 404
      ? 'notFound'
      : status === 502
      ? 'upstream'
      : 'failure';

    yield put(resendDirectChildOrganisationUserInvitationFailure(organisationSlug, userId));
    yield call(
      message.error,
      i18n.t(`directChildOrganisationAdministration:users.details.resend.${errorKey}`),
    );
  }
}

export function* doEditDirectChildOrganisationUser(
  action: ReturnType<typeof import('./actions').editDirectChildOrganisationUser>,
): any {
  const { organisationSlug, userId, edit } = action.payload;

  try {
    const service: IPEPHealthService = yield call(doCreatePEPHealthService);
    yield call(service.editOrganisationUser, organisationSlug, userId, edit);
    yield put(editDirectChildOrganisationUserSuccess(organisationSlug, userId));
    yield call(message.success, i18n.t('directChildOrganisationAdministration:users.edit.success'));
  } catch (error) {
    const status = errorStatus(error);
    const errorKey =
      status === 403
        ? 'forbidden'
        : status === 404
        ? 'notFound'
        : status === 502
        ? 'upstream'
        : 'failure';

    yield put(editDirectChildOrganisationUserFailure(organisationSlug, userId));
    yield call(
      message.error,
      i18n.t(`directChildOrganisationAdministration:users.edit.${errorKey}`),
    );
  }
}

export default function* directChildOrganisationAdministrationSaga() {
  yield takeLatest([SESSION_ESTABLISHED, LOAD_CURRENT_ORGANISATION], doLoadCurrentOrganisation);
  yield takeLatest(LOAD_DIRECT_CHILD_ORGANISATIONS, doLoadDirectChildOrganisations);
  yield takeLatest(LOAD_DIRECT_CHILD_ORGANISATION, doLoadDirectChildOrganisation);
  yield takeLatest(LOAD_DIRECT_CHILD_ORGANISATION_USERS, doLoadDirectChildOrganisationUsers);
  yield takeLatest(
    LOAD_DIRECT_CHILD_ORGANISATION_USER_DETAILS,
    doLoadDirectChildOrganisationUserDetails,
  );
  yield takeLeading(CREATE_DIRECT_CHILD_ORGANISATION, doCreateDirectChildOrganisation);
  yield takeLeading(INVITE_DIRECT_CHILD_ORGANISATION_USER, doInviteDirectChildOrganisationUser);
  yield takeEvery(EDIT_DIRECT_CHILD_ORGANISATION_USER, doEditDirectChildOrganisationUser);
  yield takeLeading(
    RESEND_DIRECT_CHILD_ORGANISATION_USER_INVITATION,
    doResendDirectChildOrganisationUserInvitation,
  );
}
